import React, { Component } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';

// Special orders a customer has placed and confirmed, waiting on the captain.
//
// One row per order (grouped by requestid), not one per line: the captain's
// decision is about the order as a whole, so Approve acts on all of its lines
// plus the order-level row at once. Lines are shown read-only for review.
//
// Opened from the Pending Orders summary card. Expand state is local to this
// modal — nothing here needs lifting into CaptainDashboard, since approving
// works on a whole group rather than on a selection.
class PendingOrdersModal extends Component {

    // confirmingId: the order whose Reject has been pressed once. Rejecting
    // cancels every line and there is no undo on this screen, so it takes a
    // second, deliberate click — inline rather than a popup, which keeps the
    // order's own items on screen while the captain decides.
    state = { expandedIds: [], confirmingId: null };

    componentDidUpdate(prevProps) {
        // A closed-then-reopened modal must not resume a half-pressed Reject.
        if (prevProps.visible && !this.props.visible && this.state.confirmingId) {
            this.setState({ confirmingId: null });
        }
    }

    isExpanded = (group, index) => {
        // The soonest two open on their own; everything else is opt-in.
        const { expandedIds } = this.state;
        if (expandedIds.includes(group.requestId)) return true;
        return index < 2 && !expandedIds.includes(`!${group.requestId}`);
    };

    toggle = (group, index) => {
        const open = this.isExpanded(group, index);
        this.setState((prev) => {
            const without = prev.expandedIds.filter(
                (id) => id !== group.requestId && id !== `!${group.requestId}`
            );
            // Auto-opened rows need an explicit "closed" marker, since simply
            // removing the id would leave them open again by position.
            return { expandedIds: [...without, open ? `!${group.requestId}` : group.requestId] };
        });
    };

    fmtDate = (value) => {
        if (!value) return '—';
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
    };

    renderOrder = (group, index) => {
        const expanded = this.isExpanded(group, index);
        const { decidingId, onApprove, onReject } = this.props;
        const busy = decidingId === group.requestId;
        // Any write in flight locks every other row: two orders decided at
        // once would race the same refresh.
        const locked = Boolean(decidingId) && !busy;
        const confirming = this.state.confirmingId === group.requestId;

        return (
            <div className="captain-pending-order captain-so-order" key={group.requestId}>
                <button
                    type="button"
                    className="captain-pending-order-header"
                    onClick={() => this.toggle(group, index)}
                    aria-expanded={expanded}
                >
                    <i className={`pi ${expanded ? 'pi-chevron-down' : 'pi-chevron-right'}`} />

                    <span className="captain-so-when">
                        <b>{this.fmtDate(group.deliveryDate)}</b>
                        {group.deliveryTime ? <span>{group.deliveryTime}</span> : null}
                    </span>

                    <span className="captain-pending-order-label">{group.orderNo}</span>

                    <span className="captain-pending-order-count">
                        {group.items.length} item{group.items.length !== 1 ? 's' : ''}
                    </span>
                </button>

                {expanded && (
                    <div className="captain-pending-order-body">
                        <div className="captain-so-meta">
                            <div><span>Customer</span>{group.customerName || '—'}</div>
                            <div><span>Phone</span>{group.phone || '—'}</div>
                            <div><span>Address</span>{group.address || '—'}</div>
                        </div>

                        <div className="captain-so-items">
                            {group.items.map((item) => (
                                <div className="captain-so-item" key={item.id}>
                                    <span className="captain-so-item-qty">&times;{item.qty}</span>
                                    <span className="captain-so-item-name">
                                        {item.name}
                                        {item.instruction
                                            ? <em className="captain-so-item-note">{item.instruction}</em>
                                            : null}
                                    </span>
                                </div>
                            ))}
                        </div>

                        <div className="captain-so-foot">
                            {/* Flagged rather than hidden: without an order-level
                                row the decision can only move the lines, and the
                                captain should know the order itself stays put. */}
                            {!group.orderRow ? (
                                <span className="captain-so-warn">
                                    <i className="pi pi-exclamation-triangle" /> No order row — lines only
                                </span>
                            ) : <span />}

                            {confirming ? (
                                <span className="captain-so-confirm">
                                    <span className="captain-so-confirm-q">
                                        Reject this order? All {group.items.length} item
                                        {group.items.length !== 1 ? 's' : ''} will be cancelled.
                                    </span>
                                    <Button
                                        label="Keep"
                                        className="p-button-text p-button-sm"
                                        disabled={busy}
                                        onClick={() => this.setState({ confirmingId: null })}
                                    />
                                    <Button
                                        label={busy ? 'Rejecting…' : 'Yes, reject'}
                                        icon={busy ? 'pi pi-spin pi-spinner' : 'pi pi-times'}
                                        className="p-button-danger p-button-sm captain-so-approve"
                                        disabled={busy}
                                        onClick={() => {
                                            this.setState({ confirmingId: null });
                                            onReject(group);
                                        }}
                                    />
                                </span>
                            ) : (
                                <span className="captain-so-actions">
                                    <Button
                                        label="Reject"
                                        icon="pi pi-times"
                                        className="p-button-outlined p-button-danger p-button-sm captain-so-approve"
                                        disabled={busy || locked || !group.items.length}
                                        onClick={() => this.setState({ confirmingId: group.requestId })}
                                    />
                                    <Button
                                        label={busy ? 'Working…' : 'Approve Order'}
                                        icon={busy ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                                        className="p-button-success captain-so-approve"
                                        disabled={busy || locked || !group.items.length}
                                        onClick={() => onApprove(group)}
                                    />
                                </span>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    render() {
        const { visible, onHide, groups = [], error, onRefresh, refreshing } = this.props;

        return (
            <Dialog
                header="Pending Orders — Confirmed by Customer"
                visible={visible}
                onHide={onHide}
                className="captain-pending-modal captain-so-modal"
                style={{ width: '92vw', maxWidth: 560 }}
                breakpoints={{ '640px': '96vw' }}
                dismissableMask
            >
                <div className="captain-so-modal-bar">
                    <span className="captain-so-sub">
                        {refreshing
                            ? 'Checking for changes…'
                            : `${groups.length} order${groups.length !== 1 ? 's' : ''} awaiting approval`}
                    </span>
                    <Button
                        icon={refreshing ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'}
                        label="Refresh"
                        className="p-button-text p-button-sm"
                        disabled={refreshing}
                        onClick={onRefresh}
                    />
                </div>

                {error ? <div className="captain-so-error">{error}</div> : null}

                {groups.length === 0 ? (
                    // While refreshing, an empty list means "not loaded yet",
                    // not "nothing to do" — saying the latter would tell the
                    // captain the queue is clear when it may not be.
                    <div className="captain-kot-empty">
                        {refreshing ? 'Loading orders…' : 'No orders waiting for approval.'}
                    </div>
                ) : (
                    <div className={`captain-so-list${refreshing ? ' captain-so-list--stale' : ''}`}>
                        {groups.map(this.renderOrder)}
                    </div>
                )}
            </Dialog>
        );
    }
}

export default PendingOrdersModal;
